import type {
  CachedItemCatalogRecord,
  CachedPurchaseOrderRecord,
  CachedRequisitionRecord,
  CachedVendorRecord,
  OfflineCacheStoreName,
  OfflineComponentTreeCacheRecord,
  OfflineComponentTreeNode,
  OfflineSyncEventRecord,
  OfflineUserSessionRecord,
} from "../../types/offline";
import type {
  EquipmentInstanceCatalogEntry,
  RequisitionItemCatalogEntry,
  RequisitionListItem,
} from "../../types/requisition";
import type { PurchaseOrderSummary } from "../../types/purchaseOrder";
import type { VendorListItem } from "../../types/vendor";
import type { VesselId } from "../../types/vessel";
import { deleteOfflineRecord, listOfflineRecords, upsertOfflineRecord } from "./schema";

function nowIso() {
  return new Date().toISOString();
}

function cacheId(prefix: string, id: string): string {
  return `${prefix}:${id}`;
}

async function replaceRecords<TRecord extends { id: string }>(
  storeName: OfflineCacheStoreName,
  records: TRecord[],
): Promise<void> {
  await Promise.all(records.map((record) => upsertOfflineRecord(storeName, record.id, record as never)));
}

export async function cacheRequisitions(
  rows: RequisitionListItem[],
  options: { vesselId: VesselId; source: CachedRequisitionRecord["source"]; syncedAt?: string },
): Promise<void> {
  const syncedAt = options.syncedAt ?? nowIso();
  await replaceRecords(
    "cachedRequisitions",
    rows.map((row) => ({
      id: cacheId("req", row.id),
      vesselId: options.vesselId,
      data: row,
      lastSyncedAt: syncedAt,
      source: options.source,
    })),
  );
}

export async function cachePurchaseOrders(
  rows: PurchaseOrderSummary[],
  options: { vesselId: VesselId; source: CachedPurchaseOrderRecord["source"]; syncedAt?: string },
): Promise<void> {
  const syncedAt = options.syncedAt ?? nowIso();
  await replaceRecords(
    "cachedPurchaseOrders",
    rows.map((row) => ({
      id: cacheId("po", row.id),
      vesselId: options.vesselId,
      data: row,
      lastSyncedAt: syncedAt,
      source: options.source,
    })),
  );
}

export async function cacheVendors(
  rows: VendorListItem[],
  options: { source: CachedVendorRecord["source"]; syncedAt?: string },
): Promise<void> {
  const syncedAt = options.syncedAt ?? nowIso();
  await replaceRecords(
    "cachedVendors",
    rows.map((row) => ({
      id: cacheId("vendor", row.id),
      vesselId: null,
      data: row,
      lastSyncedAt: syncedAt,
      source: options.source,
    })),
  );
}

export async function cacheItemCatalog(
  rows: Array<RequisitionItemCatalogEntry | EquipmentInstanceCatalogEntry>,
  options: { vesselId: VesselId; source: CachedItemCatalogRecord["source"]; syncedAt?: string },
): Promise<void> {
  const syncedAt = options.syncedAt ?? nowIso();
  await replaceRecords(
    "cachedItemCatalog",
    rows.map((row) => ({
      id: cacheId("item", row.id),
      vesselId: options.vesselId,
      data: row,
      lastSyncedAt: syncedAt,
      source: options.source,
    })),
  );
}

export async function cacheComponentTree(
  nodes: OfflineComponentTreeNode[],
  options: { vesselId: VesselId; source: OfflineComponentTreeCacheRecord["source"]; syncedAt?: string },
): Promise<void> {
  const record: OfflineComponentTreeCacheRecord = {
    id: cacheId("component-tree", String(options.vesselId)),
    vesselId: options.vesselId,
    data: nodes,
    lastSyncedAt: options.syncedAt ?? nowIso(),
    source: options.source,
  };
  await upsertOfflineRecord("cachedComponentTree", record.id, record);
}

export async function listCachedRequisitions(): Promise<CachedRequisitionRecord[]> {
  return listOfflineRecords<CachedRequisitionRecord>("cachedRequisitions");
}

export async function listCachedPurchaseOrders(): Promise<CachedPurchaseOrderRecord[]> {
  return listOfflineRecords<CachedPurchaseOrderRecord>("cachedPurchaseOrders");
}

export async function listCachedVendors(): Promise<CachedVendorRecord[]> {
  return listOfflineRecords<CachedVendorRecord>("cachedVendors");
}

export async function listCachedItemCatalog(): Promise<CachedItemCatalogRecord[]> {
  return listOfflineRecords<CachedItemCatalogRecord>("cachedItemCatalog");
}

export async function recordSyncEvent(event: OfflineSyncEventRecord): Promise<void> {
  await upsertOfflineRecord("syncEvents", event.id, event);
}

export async function saveOfflineUserSession(session: OfflineUserSessionRecord): Promise<void> {
  await upsertOfflineRecord("userSession", session.key, session);
}

export async function clearCachedRecord(storeName: OfflineCacheStoreName, key: string): Promise<void> {
  await deleteOfflineRecord(storeName, key);
}
